"use client"

import {
  getBrandMenuColumns,
  type MenuBrand,
} from "@lib/util/home-categories"
import type { NavSegment } from "@lib/util/nav-menu"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"
import {
  useId,
  useState,
  type KeyboardEvent,
} from "react"

const BRAND_FILTER_THRESHOLD = 8

const triggerClass = (open: boolean) =>
  clx(
    "relative z-[70] py-1 text-[15px] font-medium tracking-normal transition-colors",
    open
      ? "text-vx-accent after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:bg-vx-accent"
      : "text-vx-ink/80 hover:text-vx-ink"
  )

type SharedMenuControl = {
  menuId: string
  open: boolean
  onOpen: () => void
  onClose: () => void
  onCancelClose: () => void
  onScheduleClose: () => void
}

type BrandMegaMenuProps = SharedMenuControl & {
  mode: "brand"
  label?: string
  brands: MenuBrand[]
}

type SegmentMegaMenuProps = SharedMenuControl & {
  mode: "segment"
  segment: NavSegment
}

type ShopMegaMenuProps = SharedMenuControl & {
  mode: "shop"
  label?: string
  brands: MenuBrand[]
  segments: NavSegment[]
}

type MegaMenuProps =
  | BrandMegaMenuProps
  | SegmentMegaMenuProps
  | ShopMegaMenuProps

const MegaMenu = (props: MegaMenuProps) => {
  const {
    menuId,
    open,
    onOpen,
    onClose,
    onCancelClose,
    onScheduleClose,
  } = props
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null)
  const [brandFilter, setBrandFilter] = useState("")
  const [shopRailKey, setShopRailKey] = useState<string>("brands")
  const panelId = useId()

  const triggerLabel =
    props.mode === "brand"
      ? props.label || "Brand"
      : props.mode === "shop"
        ? props.label || "ร้านค้า"
        : props.segment.label

  const brands =
    props.mode === "brand" || props.mode === "shop" ? props.brands : []

  const triggerTestId = `nav-${String(triggerLabel)
    .toLowerCase()
    .replace(/\s+/g, "-")}-trigger`

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault()
      onOpen()
    }
  }

  const empty =
    props.mode === "brand"
      ? brands.length === 0
      : props.mode === "shop"
        ? brands.length === 0 && props.segments.length === 0
        : props.segment.columns.length === 0

  if (empty) {
    return null
  }

  const showingBrands =
    props.mode === "brand" ||
    (props.mode === "shop" && shopRailKey === "brands")

  const filteredBrands =
    showingBrands
      ? brandFilter.trim().length === 0
        ? brands
        : brands.filter((brand) =>
            brand.name.toLowerCase().includes(brandFilter.trim().toLowerCase())
          )
      : []

  const activeBrandId = showingBrands
    ? (selectedBrandId &&
      filteredBrands.some((brand) => brand.id === selectedBrandId)
        ? selectedBrandId
        : null) ??
      filteredBrands[0]?.id ??
      brands[0]?.id ??
      ""
    : ""

  const activeBrand = showingBrands
    ? filteredBrands.find((b) => b.id === activeBrandId) ||
      brands.find((b) => b.id === activeBrandId) ||
      filteredBrands[0] ||
      brands[0]
    : null

  const brandColumns = activeBrand
    ? getBrandMenuColumns(activeBrand.lines)
    : []

  const showBrandFilter =
    showingBrands && brands.length >= BRAND_FILTER_THRESHOLD

  const activeShopSegment =
    props.mode === "shop" && shopRailKey.startsWith("segment:")
      ? props.segments.find((s) => `segment:${s.id}` === shopRailKey) || null
      : null

  const brandLinesContent = activeBrand ? (
    <>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h3 className="text-[17px] font-semibold text-vx-ink">
          {activeBrand.name}
        </h3>
        <LocalizedClientLink
          href={`/categories/${activeBrand.handle}`}
          className="shrink-0 text-[13px] font-medium text-vx-accent hover:text-vx-ink"
          onClick={onClose}
          data-testid={`mega-menu-view-all-${activeBrand.handle}`}
        >
          ดูทั้งหมด {activeBrand.name}
        </LocalizedClientLink>
      </div>

      {brandColumns.length > 0 ? (
        <div
          className="grid w-full flex-1 gap-x-12 gap-y-6"
          style={{
            gridTemplateColumns: `repeat(${brandColumns.length}, minmax(10rem, 1fr))`,
          }}
        >
          {brandColumns.map((column, columnIndex) => (
            <ul key={columnIndex} className="flex min-w-0 flex-col gap-3">
              {column.map((line) => (
                <li key={line.id}>
                  <LocalizedClientLink
                    href={`/categories/${line.handle}`}
                    className="block text-[14px] leading-snug text-vx-ink/70 transition-colors hover:text-vx-ink"
                    onClick={onClose}
                    data-testid={`mega-menu-line-${line.handle}`}
                  >
                    {line.name}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          ))}
        </div>
      ) : (
        <p className="text-[14px] text-vx-muted">
          ยังไม่มีสายสินค้า{" "}
          <LocalizedClientLink
            href={`/categories/${activeBrand.handle}`}
            className="text-vx-accent hover:text-vx-ink"
            onClick={onClose}
          >
            ดู {activeBrand.name}
          </LocalizedClientLink>
        </p>
      )}
    </>
  ) : null

  const brandListAside = (
    <aside className="flex w-[200px] shrink-0 flex-col border-r border-vx-line bg-vx-elevated/80 py-5 medium:w-[240px]">
      {showBrandFilter && (
        <div className="px-3 pb-3">
          <label className="sr-only" htmlFor={`${panelId}-brand-filter`}>
            Filter brands
          </label>
          <input
            id={`${panelId}-brand-filter`}
            type="search"
            value={brandFilter}
            onChange={(event) => setBrandFilter(event.target.value)}
            placeholder="Filter brands"
            className="w-full border border-vx-line bg-vx-surface px-2.5 py-2 text-[13px] text-vx-ink outline-none placeholder:text-vx-muted focus:border-vx-accent"
            data-testid="mega-menu-brand-filter"
          />
        </div>
      )}
      <ul className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2">
        {filteredBrands.map((brand) => {
          const isActive = brand.id === activeBrand?.id
          return (
            <li key={brand.id}>
              <button
                type="button"
                className={clx(
                  "w-full px-3 py-3 text-left text-[15px] transition-colors",
                  isActive
                    ? "bg-vx-surface font-semibold text-vx-accent shadow-sm"
                    : "text-vx-ink/70 hover:bg-vx-surface/70 hover:text-vx-ink"
                )}
                onMouseEnter={() => setSelectedBrandId(brand.id)}
                onFocus={() => setSelectedBrandId(brand.id)}
                onClick={() => setSelectedBrandId(brand.id)}
                data-testid={`mega-menu-brand-${brand.handle}`}
              >
                {brand.name}
              </button>
            </li>
          )
        })}
      </ul>
    </aside>
  )

  const renderSegmentColumns = (segment: NavSegment) => (
    <div className="max-h-[min(70vh,560px)] overflow-y-auto p-6 medium:px-10 medium:py-8">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-[17px] font-semibold text-vx-ink">{segment.label}</h3>
        <LocalizedClientLink
          href="/store"
          className="text-[13px] font-medium text-vx-accent hover:text-vx-ink"
          onClick={onClose}
        >
          ดูร้านค้าทั้งหมด
        </LocalizedClientLink>
      </div>
      <div
        className="grid gap-x-12 gap-y-8"
        style={{
          gridTemplateColumns: `repeat(${Math.min(segment.columns.length || 1, 4)}, minmax(0, 1fr))`,
        }}
      >
        {segment.columns.map((column) => (
          <div key={column.id}>
            <LocalizedClientLink
              href={column.href}
              onClick={onClose}
              className="mb-4 block text-[16px] font-semibold text-vx-ink hover:text-vx-accent"
              data-testid={`mega-segment-brand-${column.handle}`}
            >
              {column.name}
            </LocalizedClientLink>
            <ul className="flex flex-col gap-2.5">
              {column.links.map((link) => (
                <li key={link.handle}>
                  <LocalizedClientLink
                    href={link.href}
                    onClick={onClose}
                    className="text-[14px] text-vx-ink/65 transition-colors hover:text-vx-ink"
                    data-testid={`mega-segment-link-${link.handle}`}
                  >
                    {link.label}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div
      data-menu-id={menuId}
      onMouseEnter={() => {
        onCancelClose()
        onOpen()
      }}
      onMouseLeave={onScheduleClose}
    >
      <button
        type="button"
        className={triggerClass(open)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        data-testid={triggerTestId}
        onClick={() => (open ? onClose() : onOpen())}
        onKeyDown={onTriggerKeyDown}
        onFocus={() => {
          onCancelClose()
          onOpen()
        }}
      >
        {triggerLabel}
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={triggerLabel}
          data-testid="mega-menu-panel"
          className="absolute inset-x-0 top-full z-[60] w-full"
          onMouseEnter={onCancelClose}
          onMouseLeave={onScheduleClose}
        >
          <div className="border-b border-vx-line bg-vx-surface shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
            {props.mode === "brand" && (
              <div className="content-container flex w-full max-h-[min(70vh,560px)] min-h-[280px]">
                {brandListAside}
                <div className="flex min-w-0 flex-1 flex-col overflow-y-auto p-6 medium:px-10 medium:py-8">
                  {brandLinesContent}
                </div>
              </div>
            )}

            {props.mode === "segment" && (
              <div className="content-container">
                {renderSegmentColumns(props.segment)}
              </div>
            )}

            {props.mode === "shop" && (
              <div className="content-container flex w-full max-h-[min(70vh,560px)] min-h-[280px]">
                <aside className="flex w-[160px] shrink-0 flex-col border-r border-vx-line bg-vx-elevated/80 py-5 medium:w-[200px]">
                  <ul className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2">
                    {brands.length > 0 && (
                      <li>
                        <button
                          type="button"
                          className={clx(
                            "w-full px-3 py-3 text-left text-[15px] transition-colors",
                            shopRailKey === "brands"
                              ? "bg-vx-surface font-semibold text-vx-accent shadow-sm"
                              : "text-vx-ink/70 hover:bg-vx-surface/70 hover:text-vx-ink"
                          )}
                          onMouseEnter={() => setShopRailKey("brands")}
                          onFocus={() => setShopRailKey("brands")}
                          onClick={() => setShopRailKey("brands")}
                          data-testid="mega-shop-rail-brands"
                        >
                          ตามแบรนด์
                        </button>
                      </li>
                    )}
                    {props.segments.map((segment) => {
                      const key = `segment:${segment.id}`
                      const isActive = shopRailKey === key
                      return (
                        <li key={key}>
                          <button
                            type="button"
                            className={clx(
                              "w-full px-3 py-3 text-left text-[15px] transition-colors",
                              isActive
                                ? "bg-vx-surface font-semibold text-vx-accent shadow-sm"
                                : "text-vx-ink/70 hover:bg-vx-surface/70 hover:text-vx-ink"
                            )}
                            onMouseEnter={() => setShopRailKey(key)}
                            onFocus={() => setShopRailKey(key)}
                            onClick={() => setShopRailKey(key)}
                            data-testid={`mega-shop-rail-${segment.id}`}
                          >
                            {segment.label}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                  <div className="border-t border-vx-line px-4 pt-4">
                    <LocalizedClientLink
                      href="/store"
                      onClick={onClose}
                      className="text-[13px] font-medium text-vx-accent hover:text-vx-ink"
                    >
                      เปิดร้านค้าทั้งหมด
                    </LocalizedClientLink>
                  </div>
                </aside>

                {shopRailKey === "brands" ? (
                  <div className="flex min-w-0 flex-1">
                    {brandListAside}
                    <div className="flex min-w-0 flex-1 flex-col overflow-y-auto p-6 medium:px-8 medium:py-8">
                      {brandLinesContent}
                    </div>
                  </div>
                ) : activeShopSegment ? (
                  <div className="min-w-0 flex-1">
                    {renderSegmentColumns(activeShopSegment)}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MegaMenu
